import { ARENA_CX, ARENA_CY, ARENA_H, ARENA_W } from "@duelgame/shared";

export type DecorType = "cactus" | "rocks" | "sandDunes" | "horse" | "dead";

export interface ArenaProp {
  type: DecorType;
  variant: number;
  /** Center X */
  x: number;
  /** Feet Y (bottom of sprite) */
  y: number;
  /** Horizontal flip (used by the horse). */
  facingLeft?: boolean;
}

export const DECOR_SIZE = 96;
const DECOR_GAP = 6;
const ARENA_MARGIN = 14;
const CENTER_MARGIN = 18;
/** Keep only the opening face-off spots clear of props. */
const SPAWN_CLEAR_HALF_W = 100;
const SPAWN_CLEAR_HALF_H = 60;
const HORSE_CHANCE = 0.2;
const NIGHT_PERCENT = 15;

/** Strong 32-bit avalanche hash → uniform integer. */
function hash32(n: number): number {
  let h = n >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Deterministic match lighting derived from the shared decor seed (~20%). */
export function isNightArena(seed: number): boolean {
  // Separate stream from prop placement so layout stays independent.
  return hash32((seed ^ 0x71e3c19d) >>> 0) % 100 < NIGHT_PERCENT;
}

/** Mulberry32 — deterministic 0..1 from a seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function overlapsSpawnClear(x: number, y: number): boolean {
  const half = DECOR_SIZE / 2;
  const propL = x - half;
  const propR = x + half;
  const propT = y - DECOR_SIZE;
  const propB = y;
  const clearL = ARENA_CX - SPAWN_CLEAR_HALF_W;
  const clearR = ARENA_CX + SPAWN_CLEAR_HALF_W;
  const clearT = ARENA_CY - SPAWN_CLEAR_HALF_H;
  const clearB = ARENA_CY + SPAWN_CLEAR_HALF_H;
  return propL < clearR && propR > clearL && propT < clearB && propB > clearT;
}

/**
 * Place cactus, rock, and sand-dune props on each half.
 * Per side: 2× cactus1, 1× cactus2, 3× cactus3, plus rocks/dunes.
 * ~20% chance of one horse somewhere. Bounds never overlap.
 * Same seed → identical layout on both clients.
 */
export function generateDecor(seed: number, allowHorse = true): ArenaProp[] {
  const rand = mulberry32(seed);
  const props: ArenaProp[] = [];
  const cactus: Array<Pick<ArenaProp, "type" | "variant">> = [
    ...Array.from({ length: 2 }, () => ({ type: "cactus" as const, variant: 1 })),
    { type: "cactus", variant: 2 },
    ...Array.from({ length: 3 }, () => ({ type: "cactus" as const, variant: 3 })),
  ];
  const variants: Array<Pick<ArenaProp, "type" | "variant">> = [
    ...cactus,
    ...[1, 2, 3, 4, 5].map((variant) => ({
      type: "rocks" as const,
      variant,
    })),
    ...[1, 2, 3, 4, 5].map((variant) => ({
      type: "sandDunes" as const,
      variant,
    })),
  ];
  const halfW = DECOR_SIZE / 2;

  const shuffledSlots = (xMin: number, xMax: number): Array<{ x: number; y: number }> => {
    const slots: Array<{ x: number; y: number }> = [];
    const columns = 5;
    const rows = 6;
    const minY = DECOR_SIZE + ARENA_MARGIN;
    const maxY = ARENA_H - ARENA_MARGIN;
    const xStep = (xMax - xMin) / (columns - 1);
    const yStep = (maxY - minY) / (rows - 1);
    // Small jitter makes the guaranteed-safe grid look less regular. The
    // remaining gap between adjacent 96px bounds is always >= DECOR_GAP.
    const jitterX = Math.max(0, (xStep - DECOR_SIZE - DECOR_GAP) / 2);
    const jitterY = Math.max(0, (yStep - DECOR_SIZE - DECOR_GAP) / 2);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const x = xMin + col * xStep + (rand() * 2 - 1) * jitterX;
        const y = minY + row * yStep + (rand() * 2 - 1) * jitterY;
        if (overlapsSpawnClear(x, y)) continue;
        slots.push({ x, y });
      }
    }
    for (let i = slots.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [slots[i], slots[j]] = [slots[j]!, slots[i]!];
    }
    return slots;
  };

  const leftMin = halfW + ARENA_MARGIN;
  const leftMax = ARENA_CX - halfW - CENTER_MARGIN;
  const rightMin = ARENA_CX + halfW + CENTER_MARGIN;
  const rightMax = ARENA_W - halfW - ARENA_MARGIN;
  const leftSlots = shuffledSlots(leftMin, leftMax);
  const rightSlots = shuffledSlots(rightMin, rightMax);

  variants.forEach((item, i) => {
    const slot = leftSlots[i];
    if (slot) props.push({ ...item, ...slot });
  });
  variants.forEach((item, i) => {
    const slot = rightSlots[i];
    if (slot) props.push({ ...item, ...slot });
  });

  // One optional horse in a leftover safe slot, randomly mirrored.
  if (allowHorse && rand() < HORSE_CHANCE) {
    const leftovers = [
      ...leftSlots.slice(variants.length),
      ...rightSlots.slice(variants.length),
    ];
    if (leftovers.length > 0) {
      const slot = leftovers[Math.floor(rand() * leftovers.length)]!;
      props.push({
        type: "horse",
        variant: 1,
        x: slot.x,
        y: slot.y,
        facingLeft: rand() < 0.5,
      });
    }
  }

  return props;
}
